import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { LOAN_PRODUCTS } from "@/lib/loanProducts";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from "recharts";
import { FileText, Users, Wallet, AlertCircle, TrendingUp, CheckCircle2 } from "lucide-react";

const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const COLORS = ["#5B2C82", "#E87722", "#2E7D32", "#1976D2", "#C2185B", "#F9A825"];
const productTitle = (key) => LOAN_PRODUCTS.find((p) => p.key === key)?.title || key;

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setData(data));
  }, []);

  if (!data) return <DashboardLayout role="admin"><div>Loading…</div></DashboardLayout>;

  const productData = data.by_product.map((p) => ({ name: productTitle(p.product), value: p.count }));

  return (
    <DashboardLayout role="admin">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-semibold text-ink sm:text-3xl">Operations dashboard</h1>
        <p className="mt-1 text-sm text-ink-soft">Portfolio health, pipeline, arrears — all at a glance.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Stat icon={FileText} label="Applications" value={data.totals.applications} accent="#5B2C82" />
        <Stat icon={TrendingUp} label="Pending" value={data.totals.pending} accent="#E87722" />
        <Stat icon={CheckCircle2} label="Approved" value={data.totals.approved} accent="#2E7D32" />
        <Stat icon={Users} label="Customers" value={data.totals.customers} accent="#1976D2" />
        <Stat icon={Wallet} label="Active loans" value={data.totals.active_loans} accent="#5B2C82" />
        <Stat icon={AlertCircle} label="Arrears" value={data.portfolio.arrears_count} accent="#D32F2F" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-4">
        <PortfolioStat label="Disbursed" value={fmt(data.portfolio.disbursed)} />
        <PortfolioStat label="Outstanding" value={fmt(data.portfolio.outstanding)} highlight />
        <PortfolioStat label="Collected" value={fmt(data.portfolio.collected)} />
        <PortfolioStat
          label="Portfolio at risk (PAR)"
          value={`${data.portfolio.par_pct ?? 0}%`}
          sub={fmt(data.portfolio.par_balance)}
          risk={(data.portfolio.par_pct ?? 0) >= 10}
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Panel title="Applications by product">
          {productData.length === 0 ? (
            <Empty>No data yet.</Empty>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={productData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DE" />
                <XAxis dataKey="name" hide={true} />
                <YAxis tick={{ fill: "#756F7C", fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#5B2C82" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {productData.map((p, i) => (
              <span key={p.name} className="rounded-full bg-[#F9F6F0] px-2.5 py-1 text-xs text-ink-soft">
                <span className="mr-1.5 inline-block h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                {p.name}: {p.value}
              </span>
            ))}
          </div>
        </Panel>

        <Panel title="Pipeline status mix">
          {data.totals.applications === 0 ? (
            <Empty>No applications yet.</Empty>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={[
                    { name: "Pending", value: data.totals.pending },
                    { name: "Approved", value: data.totals.approved },
                    { name: "Rejected", value: data.totals.rejected },
                  ]}
                  dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={3}
                >
                  {[COLORS[1], COLORS[2], "#D32F2F"].map((c, i) => <Cell key={i} fill={c} />)}
                </Pie>
                <Tooltip /> <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      <Panel title="Applications over time" className="mt-6">
        {data.by_month.length === 0 ? (
          <Empty>No data yet.</Empty>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DE" />
              <XAxis dataKey="month" tick={{ fill: "#756F7C", fontSize: 12 }} />
              <YAxis tick={{ fill: "#756F7C", fontSize: 12 }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#E87722" strokeWidth={3} dot={{ r: 4, fill: "#5B2C82" }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Panel>
    </DashboardLayout>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-[#E5E2DE] bg-white p-4" data-testid={`admin-stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
        <Icon className="h-4 w-4" style={{ color: accent }} />
      </div>
      <div className="mt-2 font-heading text-2xl font-bold text-ink">{value}</div>
    </div>
  );
}
function PortfolioStat({ label, value, highlight, sub, risk }) {
  const tone = risk
    ? "border-rose-300 bg-rose-50 text-rose-900"
    : highlight
      ? "border-[#5B2C82] bg-[#5B2C82] text-white"
      : "border-[#E5E2DE] bg-white text-ink";
  return (
    <div className={`rounded-xl border p-5 ${tone}`} data-testid={`portfolio-stat-${String(label).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className={`text-xs uppercase tracking-wider ${highlight && !risk ? "text-white/70" : "text-ink-muted"}`}>{label}</div>
      <div className="mt-2 font-heading text-3xl font-bold">{value}</div>
      {sub && <div className={`mt-1 text-xs ${highlight && !risk ? "text-white/70" : "text-ink-muted"}`}>{sub} at risk</div>}
    </div>
  );
}
function Panel({ title, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-[#E5E2DE] bg-white p-5 ${className}`}>
      <h2 className="mb-3 font-heading text-base font-semibold text-ink">{title}</h2>
      {children}
    </div>
  );
}
function Empty({ children }) {
  return <div className="rounded-lg bg-[#F9F6F0] px-4 py-6 text-center text-sm text-ink-soft">{children}</div>;
}
